/**
 * La única puerta por la que se decide QUÉ se manda a Umami (T7 · KAI-94).
 *
 * Conviene ser preciso con lo que este fichero garantiza y lo que no. No es el
 * único sitio que llama a `umami.track`: el pageview lo hace desde
 * `Analytics.js`, porque necesita encadenar el `referrer` con la página
 * anterior y eso es estado del componente, no de aquí. Lo que sí se cumple —y
 * es la garantía que importa— es que **toda url que llega a Umami sale de
 * `currentAnalyticsUrl()`**: un solo sitio aplica la frontera de rutas y la
 * allowlist de query.
 *
 * Existe porque tener dos caminos que construyeran su propia url ya salió caro:
 * el pageview se desinfectaba y el evento no, así que «captura-email» enviaba
 * la query cruda (token incluido) y se atribuía a la página anterior. Si algún
 * día aparece un envío que arma su url por su cuenta, esa garantía se cae.
 */

import { isTrackablePath } from "./paths";
import { buildAnalyticsUrl } from "./url";

/**
 * URL de analítica de la página en la que está el navegador AHORA, o `null`
 * si no se puede medir. Devolver `null` es la respuesta segura: quien llama no
 * envía nada.
 *
 * @param {string} [expectedPathname] ruta que el router cree que está montada.
 *   Si se pasa y no coincide con la del navegador, la navegación está a medias
 *   y no es momento de contar nada. El pageview la usa; un evento disparado por
 *   el usuario (un submit) no la necesita: ahí manda dónde está el navegador.
 */
export function currentAnalyticsUrl(expectedPathname) {
  if (typeof window === "undefined") return null;
  const { origin, pathname, search } = window.location;
  if (expectedPathname !== undefined && pathname !== expectedPathname) {
    return null;
  }
  if (!isTrackablePath(pathname)) return null;
  return buildAnalyticsUrl(origin, pathname, search);
}

/**
 * Evento personalizado con la url de la página real y la query filtrada.
 *
 * Se usa la forma función y no `track(nombre, datos)` a propósito: la rama de
 * cadena del tracker construye `{...C(), name, data}`, y ese `C()` lleva la
 * url congelada del momento en que se cargó el script, sin pasar por la
 * allowlist. La forma función admite `name` y `data` en el payload igual que
 * la de cadena, pero deja corregir la url.
 * Ver https://docs.umami.is/docs/tracker-functions
 */
export function trackEvent(name, data) {
  const url = currentAnalyticsUrl();
  if (!url) return;
  window.umami?.track((props) => ({ ...props, name, data, url }));
}
